import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Spin } from 'antd';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import WebsiteList from './pages/WebsiteList';
import WebsiteForm from './pages/WebsiteForm';
import ContentList from './pages/ContentList';
import ContentForm from './pages/ContentForm';
import FormList from './pages/FormList';
import FormSubmissions from './pages/FormSubmissions';
import UserList from './pages/UserList';
import UserForm from './pages/UserForm';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import './App.less';

// 受保护的路由组件
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" tip="加载中..." />
      </div>
    );
  }

  return isAuthenticated ? (
    <Layout>{children}</Layout>
  ) : (
    <Navigate to="/login" replace />
  );
};

// 公共路由组件
const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" tip="加载中..." />
      </div>
    );
  }

  return !isAuthenticated ? (
    <>{children}</>
  ) : (
    <Navigate to="/dashboard" replace />
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route 
            path="/" 
            element={<Navigate to="/dashboard" replace />} 
          />
          
          <Route 
            path="/login" 
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            } 
          />
          
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/websites" 
            element={
              <ProtectedRoute>
                <WebsiteList />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/websites/new" 
            element={
              <ProtectedRoute>
                <WebsiteForm />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/websites/:id/edit" 
            element={
              <ProtectedRoute>
                <WebsiteForm />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/contents" 
            element={
              <ProtectedRoute>
                <ContentList />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/contents/new" 
            element={
              <ProtectedRoute>
                <ContentForm />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/contents/:id/edit" 
            element={
              <ProtectedRoute>
                <ContentForm />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/forms" 
            element={
              <ProtectedRoute>
                <FormList />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/forms/:id/submissions" 
            element={
              <ProtectedRoute>
                <FormSubmissions />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/users" 
            element={
              <ProtectedRoute>
                <UserList />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/users/new" 
            element={
              <ProtectedRoute>
                <UserForm />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/users/:id/edit" 
            element={
              <ProtectedRoute>
                <UserForm />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="*" 
            element={<Navigate to="/dashboard" replace />} 
          />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
};

export default App;
